import { forwardRef } from "react";

import { isNil } from "lodash";

import { type CurrencyCode } from "@north-beam/nb-common";

import { MaskedInput } from "./masked-input";
import { type TextInputProps } from "./text-input";

interface CurrencyInputProps extends TextInputProps {
  readonly min?: number;
  readonly code: CurrencyCode;
  readonly decimal?: boolean;
}

export const getCurrencySymbol = (code: CurrencyCode) =>
  (0)
    .toLocaleString("EN-us", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, "");

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (props, ref) => {
    const stripCurrencySymbol = (val: string) => val.replace(/[^\d\.]/, "");

    return (
      <MaskedInput
        {...props}
        maskOptions={{
          mask: `${getCurrencySymbol(props.code)} num`,
          blocks: {
            num: {
              mask: Number,
              radix: ".",
              mapToRadix: ["."],
              thousandsSeparator: ",",
              normalizeZeros: true,
              scale: props.decimal ? 2 : 0,
              min: props.min,
            },
            validate: (v: string) => {
              if (v === null) {
                return true;
              }
              const parsed = parseFloat(stripCurrencySymbol(v));
              if (parsed !== null) {
                return true;
              }
              return false;
            },
          },
        }}
        value={
          !isNil(props.value)
            ? `${getCurrencySymbol(props.code)} ${props.value}`
            : undefined
        }
        validator={(v: string | null | undefined) => {
          if (v === null || v === undefined || v === "") {
            return true;
          }
          const value = Number(stripCurrencySymbol(v));
          if (props.min !== undefined && value < props.min) {
            return false;
          }
          return true;
        }}
        ref={ref}
      />
    );
  },
);
